import React, { memo, ReactNode, useEffect } from 'react';
import { GroupElement, ImageElement, TextElement, ShapeElement } from '@bynder-studio/render-web';
import {
    IconBuildShape,
    IconFolder,
    IconFolderMask,
    IconImage,
    IconImageMask,
    IconMask,
    IconMaskAlt3,
    IconTextField,
    IconTextMask,
} from '@bynder/icons';
import { Thumbnail } from '@bynder/design-system';
import useEditor from '~/hooks/useEditor';
import { isUsedAsMask } from '~/common/editor/helpers/elementtree';
import useForceUpdate from '~/hooks/useForceUpdate';
import useObjectUrl from 'packages/hooks/useObjectUrl';

type Props = {
    element: TextElement | ImageElement | GroupElement | ShapeElement;
    src?: string;
};

const GenerateIcon = memo(({ element: layerElement, src }: Props) => {
    const urlObject = useObjectUrl(src);
    const forceUpdate = useForceUpdate();
    const { creativeModel } = useEditor();

    let icon: ReactNode = null;
    const isElementMasked = layerElement?.mask !== null;
    const isMask = isUsedAsMask(creativeModel, layerElement);

    useEffect(() => {
        return creativeModel.onElementUpdate(layerElement.id, () => {
            forceUpdate();
        });
    }, [forceUpdate, creativeModel]);

    useEffect(() => {
        if (!layerElement.mask?.elementId) {
            return;
        }

        return creativeModel.onElementUpdate(layerElement.mask?.elementId, () => {
            forceUpdate();
        });
    }, [forceUpdate, creativeModel, layerElement.mask?.elementId]);

    if (isMask) {
        icon = <IconMask />;
    } else {
        if (layerElement instanceof TextElement) {
            icon = isElementMasked ? <IconTextMask /> : <IconTextField />;
        }

        if (layerElement instanceof ImageElement) {
            if (isElementMasked) {
                icon = <IconImageMask />;
            } else if (urlObject) {
                icon = <Thumbnail imageUrl={urlObject} variant="content" size="xs" />;
            } else {
                icon = <IconImage />;
            }
        }

        if (layerElement instanceof GroupElement) {
            icon = isElementMasked ? <IconFolderMask /> : <IconFolder />;
        }

        if (layerElement instanceof ShapeElement) {
            icon = isElementMasked ? <IconMaskAlt3 /> : <IconBuildShape />;
        }
    }

    return icon;
});

export default GenerateIcon;
